import { useState, useEffect } from 'react';
import { savePubInfo, GetAdminVenueDataOutputType } from 'zite-endpoints-sdk';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Loader2, Save } from 'lucide-react';

type Info = GetAdminVenueDataOutputType['info'];

const DEFAULT_HOURS = `Monday: 12:00 - 23:00
Tuesday: 12:00 - 23:00
Wednesday: 12:00 - 23:00
Thursday: 12:00 - 23:00
Friday: 12:00 - 00:00
Saturday: 11:00 - 00:00
Sunday: 11:00 - 22:30`;

export default function VenuePubInfoForm({ info, onSaved }: { info: Info; onSaved: () => void }) {
  const [form, setForm] = useState({
    pubName: 'The Rattlebone',
    addressLine1: '',
    addressLine2: '',
    town: '',
    county: '',
    postcode: '',
    phone: '',
    email: '',
    website: '',
    openingHours: DEFAULT_HOURS,
    about: '',
    emergencyContact: '',
    socialLinks: '',
    logoUrl: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (info) {
      setForm({
        pubName: info.pubName || 'The Rattlebone',
        addressLine1: info.addressLine1 || '',
        addressLine2: info.addressLine2 || '',
        town: info.town || '',
        county: info.county || '',
        postcode: info.postcode || '',
        phone: info.phone || '',
        email: info.email || '',
        website: info.website || '',
        openingHours: info.openingHours || DEFAULT_HOURS,
        about: info.about || '',
        emergencyContact: info.emergencyContact || '',
        socialLinks: info.socialLinks || '',
        logoUrl: info.logoUrl || '',
      });
    }
  }, [info]);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    if (!form.pubName.trim()) { toast.error('Pub name is required'); return; }
    setSaving(true);
    try {
      await savePubInfo({ ...form, id: info?.id });
      toast.success('Pub info saved');
      onSaved();
    } catch (e: any) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div>
      <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1.5 block">{label}</label>
      {children}
    </div>
  );

  return (
    <div className="space-y-5 max-w-2xl">
      <Field label="Pub Name *">
        <Input value={form.pubName} onChange={set('pubName')} placeholder="The Rattlebone" />
      </Field>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Address Line 1">
          <Input value={form.addressLine1} onChange={set('addressLine1')} placeholder="Street address" />
        </Field>
        <Field label="Address Line 2">
          <Input value={form.addressLine2} onChange={set('addressLine2')} placeholder="Village / area (optional)" />
        </Field>
        <Field label="Town">
          <Input value={form.town} onChange={set('town')} placeholder="e.g. Malmesbury" />
        </Field>
        <Field label="County">
          <Input value={form.county} onChange={set('county')} placeholder="e.g. Wiltshire" />
        </Field>
        <Field label="Postcode">
          <Input value={form.postcode} onChange={set('postcode')} placeholder="e.g. SN16 0HB" />
        </Field>
        <Field label="Phone">
          <Input value={form.phone} onChange={set('phone')} placeholder="+44 1666 xxx xxx" />
        </Field>
        <Field label="Email">
          <Input value={form.email} onChange={set('email')} placeholder="hello@therattlebone.co.uk" />
        </Field>
        <Field label="Website">
          <Input value={form.website} onChange={set('website')} placeholder="https://therattlebone.co.uk" />
        </Field>
      </div>

      <Field label="Opening Hours (one line per day: Monday: 12:00 - 23:00)">
        <Textarea value={form.openingHours} onChange={set('openingHours')} rows={8} placeholder={DEFAULT_HOURS} className="font-mono text-sm" />
      </Field>

      <Field label="About The Pub">
        <Textarea value={form.about} onChange={set('about')} rows={4} placeholder="A short description of the pub, history, ethos..." />
      </Field>

      <Field label="Emergency Contact">
        <Input value={form.emergencyContact} onChange={set('emergencyContact')} placeholder="e.g. 999 for emergencies · Internal: ext 200" />
      </Field>

      <Field label="Social Media Links (one per line)">
        <Textarea value={form.socialLinks} onChange={set('socialLinks')} rows={3} placeholder="Facebook: https://facebook.com/...\nInstagram: https://instagram.com/...\nTwitter: https://twitter.com/..." />
      </Field>

      <Field label="Pub Logo URL">
        <Input value={form.logoUrl} onChange={set('logoUrl')} placeholder="https://..." />
        {form.logoUrl && (
          <img src={form.logoUrl} alt="Logo preview" className="mt-2 h-12 object-contain rounded border border-border" />
        )}
      </Field>

      <Button onClick={handleSave} disabled={saving} className="gap-2">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save Pub Info</>}
      </Button>
    </div>
  );
}
